import React from 'react';
import Diamond from './Diamond';

// Gate data for each tree
const treeData = {
  location: {
    title: "Where Should I Live?",
    subtitle: "BINARY GAME DECISION TREE",
    domain: "LOCATION",
    gates: [
      {
        id: "01",
        type: "YES/NO",
        question: "Do you have a mission that requires physical presence in a specific geography?",
        subtext: "Not a preference. A mission. Something you're building, leading, or becoming that is tied to place."
      },
      {
        id: "02",
        type: "YES/NO",
        question: "Is that mission active right now - not hypothetical, not 'someday'?",
        subtext: "If it's future-tense, you're not choosing where to live. You're choosing where to wait."
      },
      {
        id: "03",
        type: "THIS/THAT",
        question: "Are you building for visibility or privacy?",
        subtext: "Some missions require being seen. Others require being hidden. Which one is yours?",
        yesLabel: "VISIBLE BUILDER",
        noLabel: "PRIVATE BUILDER"
      },
      {
        id: "A",
        type: "YES/NO",
        question: "Can you operate in this city without draining your runway or health?",
        subtext: "Cost of living, lifestyle overhead, access to support systems. Can you actually sustain the mission here?",
        pathLabel: "VISIBLE BUILDER PATH"
      },
      {
        id: "B",
        type: "YES/NO",
        question: "Does this city contain the leverage points your mission requires?",
        subtext: "Network, access, iteration speed, influence."
      }
    ],
    result: "You've found your city. The place that matches your mission, your visibility posture, and your operational needs."
  },
  launch: {
    title: "Should I Launch While Rebuilding?",
    subtitle: "BINARY GAME DECISION TREE",
    domain: "LAUNCH DECISION",
    gates: [
      {
        id: "01",
        type: "YES/NO",
        question: "Is this a real product that solves a real problem - or a distraction from facing something harder?",
        inverted: true,
        yesLabel: "DISTRACTION",
        noLabel: "REAL PRODUCT"
      },
      {
        id: "02",
        type: "YES/NO",
        question: "Am I launching because I need the money - or because this is the right time to ship?",
        inverted: true,
        yesLabel: "NEED MONEY",
        noLabel: "RIGHT TIME"
      },
      {
        id: "03",
        type: "YES/NO",
        question: "Am I hiding behind 'building' to avoid being seen - or is the work genuinely not ready?",
        inverted: true,
        yesLabel: "HIDING",
        noLabel: "NOT READY YET"
      },
      {
        id: "04",
        type: "YES/NO",
        question: "Will launching this right now make my situation worse - financially, emotionally, or reputationally?",
        inverted: true,
        yesLabel: "MAKES IT WORSE",
        noLabel: "DOESN'T HURT"
      },
      {
        id: "05",
        type: "YES/NO",
        question: "Is the discomfort I feel about launching just fear of being seen - or a real signal that something's wrong?",
        inverted: true,
        yesLabel: "REAL SIGNAL",
        noLabel: "JUST FEAR"
      },
      {
        id: "06",
        type: "YES/NO",
        question: "Do I have a clear next step if this works? And a clear next step if it doesn't?",
        inverted: true,
        yesLabel: "NO PLAN",
        noLabel: "HAVE PLAN"
      },
      {
        id: "07",
        type: "YES/NO",
        question: "Am I ready to let this be imperfect and still stand behind it?",
        inverted: true,
        yesLabel: "NOT READY",
        noLabel: "REDUCE DISCOMFORT"
      }
    ],
    result: "Launch. The discomfort you feel is not a stop sign - it's the tax on doing something real while your life isn't perfect."
  },
  mini: {
    title: "Binary Game Introduction",
    subtitle: "INTRODUCTORY DECISION TREE",
    domain: "INTRODUCTION",
    gates: [
      {
        id: "01",
        type: "YES/NO",
        question: "Do you make important decisions?"
      },
      {
        id: "02",
        type: "YES/NO",
        question: "Do bad ones cost you time, money, or energy?"
      },
      {
        id: "03",
        type: "YES/NO",
        question: "Do you want to make better decisions?"
      },
      {
        id: "04",
        type: "YES/NO",
        question: "Do you want help making better decisions?"
      },
      {
        id: "05",
        type: "YES/NO",
        question: "Does every major area of your life have rules that work under pressure?"
      }
    ],
    result: "Most people stop here. Most people say yes to the first four and stop at the fifth. That gap is exactly what Binary Game closes."
  },
  spending: {
    title: "Should I Spend This Money?",
    subtitle: "RESOURCE RULES SYSTEM",
    domain: "SPENDING",
    isZoneBased: true,
    initialGate: {
      id: "01",
      type: "YES/NO",
      question: "Is this groceries, gas, or rent?",
      subtext: "Not eating out. Not road trips. Groceries for the house, gas for the car, rent for the roof.",
      yesResult: "Spend. No further gates. These are locked expenses. They never get cut in any zone."
    },
    zones: ["RED", "YELLOW-RED", "YELLOW", "GREEN-YELLOW", "GREEN"],
    zoneNote: "If NO to Gate 01, select your cash zone and follow that path. Each zone has different rules for credit cards, subscriptions, debts, outsourcing, and discretionary spending.",
    result: "Decision complete. Follow the path that matches your current cash zone."
  }
};

const StaticTreeView = ({ treeId, onClose }) => {
  const tree = treeData[treeId];
  
  if (!tree) {
    return (
      <div className="static-tree-overlay">
        <div className="static-tree-container">
          <button className="static-tree-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <p>Tree not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="static-tree-overlay" data-testid="static-tree-view">
      <div className="static-tree-container">
        {/* Close button */}
        <button className="static-tree-close" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Header */}
        <div className="static-tree-header">
          {tree.domain && <p className="static-tree-domain">{tree.domain}</p>}
          <h1 className="static-tree-title">
            <Diamond color="#0057FF" size="md" /> {tree.title}
          </h1>
          <p className="static-tree-subtitle">{tree.subtitle} // FULL VIEW</p>
        </div>

        {/* Tree Canvas */}
        <div className="static-tree-canvas">
          {/* START Node */}
          <div className="static-tree-start">START</div>
          <div className="static-tree-line" />

          {/* Special handling for zone-based trees (spending) */}
          {tree.isZoneBased ? (
            <>
              {/* Initial Gate */}
              <div className="static-gate-section">
                <div className="static-gate-card">
                  <div className="static-gate-spine" />
                  <div className="static-gate-content">
                    <span className="static-gate-label">
                      GATE {tree.initialGate.id} | {tree.initialGate.type}
                    </span>
                    <p className="static-gate-question">{tree.initialGate.question}</p>
                    {tree.initialGate.subtext && <p className="static-gate-subtext">{tree.initialGate.subtext}</p>}
                    <div className="static-gate-buttons">
                      <div className="static-btn static-btn-yes">
                        <Diamond color="#FFFFFF" size="xs" />
                        YES
                      </div>
                      <div className="static-btn static-btn-no">NO</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* YES Result */}
              <div className="static-connector">
                <div className="static-tree-line" />
                <span className="static-connector-label">YES → RESULT</span>
                <div className="static-tree-line" />
              </div>
              <div className="static-result" style={{ marginBottom: '32px' }}>
                <Diamond color="#0057FF" size="lg" />
                <p className="static-result-text">{tree.initialGate.yesResult}</p>
              </div>

              {/* Zone Selection Info */}
              <div className="static-connector">
                <div className="static-tree-line" style={{ background: 'var(--color-rule)' }} />
                <span className="static-connector-label" style={{ color: 'var(--color-muted)' }}>NO → ZONE SELECT</span>
                <div className="static-tree-line" style={{ background: 'var(--color-rule)' }} />
              </div>
              
              <div className="static-gate-card" style={{ marginBottom: '16px' }}>
                <div className="static-gate-spine" />
                <div className="static-gate-content">
                  <span className="static-gate-label">GATE 02 | SELECT ZONE</span>
                  <p className="static-gate-question">What zone am I in?</p>
                  <p className="static-gate-subtext">{tree.zoneNote}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                    {tree.zones.map(zone => (
                      <span key={zone} style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '6px 12px',
                        background: 'var(--color-ghost)',
                        color: 'var(--color-mid)',
                        letterSpacing: '0.05em'
                      }}>
                        {zone}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '12px',
                color: 'var(--color-mid)',
                fontStyle: 'italic',
                marginTop: '16px'
              }}>
                This tree branches into 5 different paths based on your cash zone. Use the interactive "Walk Through" mode to explore each path.
              </p>
            </>
          ) : (
            /* Regular gates for non-zone-based trees */
            <>
              {tree.gates.map((gate, index) => (
                <div key={gate.id} className="static-gate-section">
                  {/* Path Label if exists */}
                  {gate.pathLabel && (
                    <div className="static-path-label">
                      <div className="static-tree-line" />
                      <span className="static-path-name">{gate.pathLabel}</span>
                      <div className="static-tree-line" />
                    </div>
                  )}

              {/* Gate Card */}
              <div className="static-gate-card">
                <div className="static-gate-spine" />
                <div className="static-gate-content">
                  <span className="static-gate-label">
                    GATE {gate.id} | {gate.type}
                  </span>
                  <p className="static-gate-question">{gate.question}</p>
                  {gate.subtext && <p className="static-gate-subtext">{gate.subtext}</p>}
                  
                  {/* Static buttons (display only) */}
                  <div className="static-gate-buttons">
                    <div className="static-btn static-btn-yes">
                      {!gate.inverted && <Diamond color="#FFFFFF" size="xs" />}
                      {gate.yesLabel || 'YES'}
                    </div>
                    <div className="static-btn static-btn-no">
                      {gate.noLabel || 'NO'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Connector */}
              {index < tree.gates.length - 1 && (
                <div className="static-connector">
                  <div className="static-tree-line" />
                  <span className="static-connector-label">→</span>
                  <div className="static-tree-line" />
                </div>
              )}
            </div>
          ))}

          {/* Result */}
          <div className="static-result-section">
            <div className="static-connector">
              <div className="static-tree-line" />
              <span className="static-connector-label">RESULT</span>
              <div className="static-tree-line" />
            </div>
            <div className="static-result">
              <Diamond color="#0057FF" size="lg" />
              <p className="static-result-text">{tree.result}</p>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaticTreeView;
